// Phase 49 — Stage 4 + Stage 5: capability-based UI gating.
//
// The single hook the UI uses to decide whether to show/hide/enable a control.
// Backed by roles.ts (the source of truth that mirrors the DB exactly), it
// resolves the current user's level for a capability in their active
// sub-account and answers has_capability-style questions.
//
// Stage 5: the hook now reflects PER-MEMBER OVERRIDES for the current user, so
// UI gating matches RLS exactly. It loads the caller's own effective
// capabilities via member_effective_capabilities(their team_member_id) and
// feeds the resolver's override slot. Owners still short-circuit to 'full'
// (capabilityLevel handles that and we skip the fetch for them). The query is
// shared across all hook consumers via TanStack Query (one fetch per member).

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../providers/AuthProvider'
import { supabase } from '../lib/supabase'
import {
  capabilityLevel,
  can as canResolver,
  normalizeRole,
  type CapabilityKey,
  type CapabilityLevel,
  type CapabilityOverrides,
  type TeamRole,
} from '../lib/roles'

export interface UseCapability {
  // The current user's role in the active sub-account (null while loading).
  role: TeamRole | null
  // ROLE-GATE-2: the resolved per-member overrides, exposed so the NAV can
  // gate on exactly what AuthGuard gates on. Null for owners (immune) and
  // while the fetch is in flight.
  overrides: CapabilityOverrides | null
  // Resolved level for a capability: 'full' | 'own' | 'view' | 'none'.
  level: (capability: CapabilityKey | string) => CapabilityLevel
  // has_capability semantics: full satisfies any min; own satisfies own/view;
  // view satisfies view. Defaults to min='view'.
  can: (capability: CapabilityKey | string, min?: 'full' | 'own' | 'view') => boolean
}

interface EffectiveRow {
  capability_key: string
  override: boolean | null
}

export function useCapability(): UseCapability {
  const { currentTeamRole, currentSubAccountId, currentTeamMember } = useAuth()
  const teamMemberId = currentTeamMember?.id ?? null
  // Owners ignore overrides (always full) so we never fetch for them.
  const isOwner = currentTeamRole ? normalizeRole(currentTeamRole) === 'owner' : false

  const overridesQuery = useQuery({
    queryKey: ['member_effective_capabilities', teamMemberId],
    enabled: !!teamMemberId && !isOwner,
    staleTime: 60_000,
    queryFn: async (): Promise<EffectiveRow[]> => {
      // RPC + table are not in the generated types yet; cast at this boundary.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc('member_effective_capabilities', {
        p_team_member_id: teamMemberId,
      })
      if (error) throw new Error(error.message)
      return (data ?? []) as EffectiveRow[]
    },
  })

  const overrides = useMemo<CapabilityOverrides | null>(() => {
    if (isOwner) return null
    const rows = overridesQuery.data
    if (!rows) return null
    const map: CapabilityOverrides = {}
    for (const r of rows) {
      if (r.override !== null) map[r.capability_key] = r.override
    }
    return map
  }, [overridesQuery.data, isOwner])

  return useMemo<UseCapability>(() => {
    const role = currentTeamRole
    return {
      role,
      overrides,
      level: (capability) => capabilityLevel(role, capability, overrides),
      can: (capability, min = 'view') => canResolver(role, capability, { min, overrides }),
    }
    // currentSubAccountId is in the dep list so switching accounts re-resolves
    // even if the role string happens to match across accounts.
  }, [currentTeamRole, currentSubAccountId, overrides])
}
